/**
 * XMR price quotes with a Kraken -> CoinGecko -> localStorage-cache fallback chain.
 *
 * Kraken market codes (internal only, users never see them):
 *   XXMRZUSD = XMR/USD, XXMRZEUR = XMR/EUR, XXMRXXBT = XMR/BTC.
 *
 * 24h change is computed client-side from Kraken's `o` (today's open):
 *   (last - open) / open * 100 — USD and EUR only. The BTC leg never shows
 *   a change (locked spec) to avoid thin-pair open-price noise.
 * CoinGecko's free endpoint carries no change data -> change is null there.
 */

export type XmrPair = 'USD' | 'EUR' | 'BTC';

export interface XmrQuote {
  usd: number;
  eur: number;
  btc: number;
  /** 24h % change vs open; null when unavailable (CoinGecko, BTC leg). */
  chgUsd: number | null;
  chgEur: number | null;
  /** ms epoch of when the quote was fetched. */
  updatedAt: number;
  source: 'kraken' | 'coingecko' | 'cache';
}

export const PRICE_CACHE_KEY = 'stashstr-xmr-price';

export function formatQuote(pair: XmrPair, q: XmrQuote): string {
  switch (pair) {
    case 'USD':
      return `$${q.usd.toFixed(2)}`;
    case 'EUR':
      return `€${q.eur.toFixed(2)}`;
    case 'BTC':
      return `${q.btc.toFixed(6)} BTC`;
  }
}

export function formatChange(chg: number | null): string {
  if (chg === null || !Number.isFinite(chg)) return '—';
  return `${chg >= 0 ? '+' : ''}${chg.toFixed(1)}%`;
}

export function isStale(updatedAt: number, now: number = Date.now()): boolean {
  return now - updatedAt > 5 * 60_000;
}

function validNumbers(q: Partial<XmrQuote>): q is XmrQuote {
  return (
    Number.isFinite(q.usd) &&
    Number.isFinite(q.eur) &&
    Number.isFinite(q.btc) &&
    typeof q.updatedAt === 'number' &&
    Number.isFinite(q.updatedAt)
  );
}

export function readPriceCache(): XmrQuote | undefined {
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(PRICE_CACHE_KEY) ?? 'null',
    ) as Partial<XmrQuote> | null;
    if (parsed && validNumbers(parsed)) return parsed as XmrQuote;
    return undefined;
  } catch {
    return undefined;
  }
}

export function writePriceCache(q: XmrQuote): void {
  try {
    window.localStorage.setItem(PRICE_CACHE_KEY, JSON.stringify(q));
  } catch {
    // storage unavailable — non-fatal
  }
}

interface KrakenTicker {
  error: unknown[];
  result?: Record<
    string,
    { c?: string[]; o?: string | string[] } | undefined
  >;
}

/** Kraken fields arrive as strings or string arrays — normalize to one string. */
function first(v: string | string[] | undefined): string {
  if (Array.isArray(v)) return v[0] ?? '';
  return v ?? '';
}

/** Kraken pair keys appear in both forms depending on API version. */
function krakenPair(result: NonNullable<KrakenTicker['result']>, base: 'XXMRZUSD' | 'XXMRZEUR') {
  return result[base] ?? result[base.replace(/^XXMRZ/, 'XMR')];
}

function krakenChange(lastStr: string, openStr: string): number | null {
  const last = parseFloat(lastStr);
  const open = parseFloat(openStr);
  if (!Number.isFinite(last) || !Number.isFinite(open) || open === 0) return null;
  return ((last - open) / open) * 100;
}

async function fetchKraken(signal: AbortSignal): Promise<XmrQuote> {
  const res = await fetch(
    'https://api.kraken.com/0/public/Ticker?pair=XMRUSD,XMREUR,XXMRXXBT',
    { signal },
  );
  if (!res.ok) throw new Error(`Kraken HTTP ${res.status}`);
  const body = (await res.json()) as KrakenTicker;
  if (!body.error || body.error.length > 0 || !body.result) {
    throw new Error('Kraken API error');
  }
  const usd = krakenPair(body.result, 'XXMRZUSD');
  const eur = krakenPair(body.result, 'XXMRZEUR');
  const btc = body.result['XXMRXXBT'];
  const usdLast = parseFloat(first(usd?.c));
  const eurLast = parseFloat(first(eur?.c));
  const btcLast = parseFloat(first(btc?.c));
  if (!Number.isFinite(usdLast) || !Number.isFinite(eurLast) || !Number.isFinite(btcLast)) {
    throw new Error('Kraken parse failed');
  }
  return {
    usd: usdLast,
    eur: eurLast,
    btc: btcLast,
    chgUsd: krakenChange(first(usd?.c), first(usd?.o)),
    chgEur: krakenChange(first(eur?.c), first(eur?.o)),
    // BTC leg never shows a change (locked spec)
    updatedAt: Date.now(),
    source: 'kraken',
  };
}

interface CoinGeckoPrice {
  monero?: { usd?: number; eur?: number; btc?: number };
}

async function fetchCoingecko(signal: AbortSignal): Promise<XmrQuote> {
  const res = await fetch(
    'https://api.coingecko.com/api/v3/simple/price?ids=monero&vs_currencies=usd,eur,btc',
    { signal },
  );
  if (!res.ok) throw new Error(`CoinGecko HTTP ${res.status}`);
  const body = (await res.json()) as CoinGeckoPrice;
  const m = body.monero;
  if (
    !m ||
    !Number.isFinite(m.usd) ||
    !Number.isFinite(m.eur) ||
    !Number.isFinite(m.btc)
  ) {
    throw new Error('CoinGecko parse failed');
  }
  return {
    usd: m.usd,
    eur: m.eur,
    btc: m.btc,
    chgUsd: null,
    chgEur: null,
    updatedAt: Date.now(),
    source: 'coingecko',
  };
}

/** Kraken -> CoinGecko -> cache. Throws only when all three fail. */
export async function fetchXmrQuote(signal: AbortSignal): Promise<XmrQuote> {
  try {
    return await fetchKraken(signal);
  } catch (krakenError) {
    try {
      return await fetchCoingecko(signal);
    } catch (coingeckoError) {
      const cached = readPriceCache();
      if (cached) return { ...cached, source: 'cache' };
      throw krakenError instanceof Error ? krakenError : coingeckoError;
    }
  }
}
